"use client";
import { formatDateToUnix, formatUnixToDate } from "@/utils/format.utils";
import { InputLabelProps } from "@mui/material";
import { DateTimePickerProps } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, memo } from "react";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import AppDateTimePicker from "../AppDateTimePicker";
import AppTextField from "../AppTextField";
import AppFromLabelField from "./AppFromLabelField";

const AppFormControlDateTimePicker = <T extends FieldValues>({
  label,
  control,
  name,
  rules,
  controlProps,
  labelProps,
  datePickerProps,
  classNameContainer,
  onChangeValueForm,
  helper,
}: AppFormControlDateTimePickerProps<T>) => {
  return (
    <AppFromLabelField
      label={label}
      labelProps={{
        ...labelProps,
      }}
      className={classNameContainer}
      required={labelProps?.required}
      helper={helper}
    >
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({
          field: { value, onChange, ...otherFieldProps },
          fieldState: { error: errorField },
        }) => {
          return (
            <AppDateTimePicker
              {...otherFieldProps}
              value={value ? formatUnixToDate(value) : null}
              onChange={(date) => {
                let valueDate: Date | null = null;
                if (date) {
                  valueDate = new Date(
                    dayjs(date).year(),
                    dayjs(date).month(),
                    dayjs(date).date(),
                    dayjs(date).hour(),
                    dayjs(date).minute()
                  );
                }

                const unixTimestamp = valueDate
                  ? formatDateToUnix(valueDate)
                  : null;
                onChange(unixTimestamp);
                if (onChangeValueForm && onChangeValueForm instanceof Function)
                  onChangeValueForm(unixTimestamp);
              }}
              slots={{
                textField: AppTextField,
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(errorField),
                  helperText: errorField?.message || "",
                },
              }}
              {...datePickerProps}
            />
          );
        }}
        {...controlProps}
      />
    </AppFromLabelField>
  );
};

type AppFormControlDateTimePickerProps<T extends FieldValues> = {
  label?: string | ReactNode;
  control: Control<any>;
  name: FieldPath<T>;
  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  labelProps?: InputLabelProps;
  datePickerProps?: DateTimePickerProps<Dayjs>;
  classNameContainer?: string;
  onChangeValueForm?: (value: Number | null) => void;
  helper?: string | ReactNode;
};

export default memo(AppFormControlDateTimePicker);
